import { Injectable } from '@angular/core';
import {
    PhotovoltaicSystemGetById,
    PlanningGetById,
    ProductGetById,
    ProjectResponseGetById,
    VariantGetById
} from '@nx-customer-apps/api-planning-projects';
import { defaultVariantNameKey, firstVariantNameKey, secondVariantNameKey, thirdVariantNameKey } from '@nx-customer-apps/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { isMaxVariantQuantity, MAX_VARIANT_QUANTITY, CREATE_NEW_VARIANT_ID, getOrder, isMinVariantQuantity } from '../../../shared/utils';
import {
    DuplicatePlanningOption,
    DuplicatePlanningOptionValue,
    PlanningWithTemplateAttributes,
    VariantWithTemplateAttributes
} from '@nx-customer-apps/shared/interfaces';
import { ApplicationId, ProductType } from '@nx-customer-apps/components';
import { VariantsService } from './variants.service';
import { LodashService } from '@nx-customer-apps/shared/services';
import { RemoteConfigParameter, VirtualPlanningId } from '@nx-customer-apps/shared/enums';
import { UnitConversionService } from './unit-conversion.service';
import { FormToolsService, RemoteConfigService } from '../../../services';
import { NgxMaskPipe } from 'ngx-mask';

@Injectable({
    providedIn: 'root'
})
export class VariantsMapper {
    private maskPipe: NgxMaskPipe;

    constructor(
        private translate: TranslateService,
        private unitConversion: UnitConversionService,
        private formToolsService: FormToolsService,
        private remoteConfig: RemoteConfigService
    ) {
        this.maskPipe = new NgxMaskPipe();
    }

    public toVariantsWithTemplateAttributes(variants: VariantGetById[]): VariantWithTemplateAttributes[] {
        return variants.map((variant, index) => ({
            ...variant,
            name: this.getName(index),
            plannings: this.toPlanningsWithTemplateAttributes({ variants, targetVariant: variant, plannings: variant.plannings }),
            duplicateVariantDisabled: isMaxVariantQuantity(variants.length),
            deleteVariantDisabled: isMinVariantQuantity(variants.length) || variant.isSelected
        }));
    }

    public getName(index: number) {
        const variantNames: string[] = [firstVariantNameKey, secondVariantNameKey, thirdVariantNameKey];
        return this.translate.instant(variantNames[index]);
    }

    private getPlanningDuplicationOptions(options: {
        variants: VariantGetById[];
        variantToSkipId: string;
        planning: PlanningGetById;
    }): DuplicatePlanningOption[] {
        const { variants, variantToSkipId, planning } = options;

        const newVariantSource: DuplicatePlanningOptionValue = {
            id: CREATE_NEW_VARIANT_ID,
            newVariantRequest: {
                order: getOrder(variants),
                name: this.translate.instant(defaultVariantNameKey)
            }
        };

        const allVariantsHavePlanningOfThisAppId = variants.every(
            variantItem => variantItem.plannings?.find(planningItem => planningItem.applicationId === planning.applicationId)
        );
        const hasLessVariantsThanMaxQuantity = variants.length < MAX_VARIANT_QUANTITY;
        const optionsSource =
            hasLessVariantsThanMaxQuantity && allVariantsHavePlanningOfThisAppId ? [...variants, newVariantSource] : variants;

        return optionsSource
            .map((variant, index) => ({
                value: {
                    id: variant.id,
                    newVariantRequest: (variant as DuplicatePlanningOptionValue).newVariantRequest
                },
                disabled: VariantsService.isUserVariant(variant as VariantGetById),
                info: (variant as DuplicatePlanningOptionValue).newVariantRequest
                    ? this.translate.instant('VARIANT_TILE.CREATE_NEW_VARIANT_ON_DUPLICATION')
                    : null,
                label: this.getName(index)
            }))
            .filter(item => item.value.id !== variantToSkipId);
    }

    public hasPVandEVPlanning(project: ProjectResponseGetById | undefined): boolean {
        return Boolean(project?.building.electricVehicles?.length || project?.building.photovoltaicSystems?.length);
    }

    public getPVandEVPlanning(project: ProjectResponseGetById | undefined): Partial<PlanningWithTemplateAttributes> | undefined {
        if (!project || !this.hasPVandEVPlanning(project)) {
            return;
        }

        const electricVehicles = this.electricVehicleToProducts(project);
        const photovoltaicSystems = this.photovoltaicSystemToProducts(project);

        return {
            hasMenu: false,
            isDisabled: true,
            isVirtual: true,
            planningId: VirtualPlanningId.ExistingPVandEV,
            products: [...electricVehicles, ...photovoltaicSystems] as ProductGetById[]
        };
    }

    private electricVehicleToProducts = (project: ProjectResponseGetById) => {
        return (
            project?.building.electricVehicles?.map(vehicle => {
                const productName = [
                    vehicle.name,
                    this.unitConversion.consumptionPerDistanceByLocale(vehicle.consumption),
                    this.unitConversion.distancePerYearByLocale(vehicle.annualDrivingDistance)
                ]
                    .filter(item => !LodashService.isNil(item))
                    .join(', ')
                    .replace(',', ''); // Removing first comma

                return {
                    productName,
                    id: vehicle.id,
                    quantity: 1
                };
            }) || []
        );
    };

    private photovoltaicSystemToProducts = (project: ProjectResponseGetById) => {
        return (
            project?.building.photovoltaicSystems?.map(system => {
                const productName = this.getPhotovoltaicSystemName(system);
                return {
                    productName,
                    id: system.id,
                    quantity: 1
                };
            }) || []
        );
    };

    private getPhotovoltaicSystemName(system: PhotovoltaicSystemGetById) {
        const nameParts = [system.name];

        if (!LodashService.isNil(system.azimuth)) {
            const azimuth = `${this.translate.instant('VARIANT_TILE.AZIMUTH')} ${this.addUnit({
                value: system.azimuth,
                key: 'MEASUREMENT_UNITS.DEGREE'
            })}`;
            nameParts.push(azimuth);
        }

        if (!LodashService.isNil(system.tiltAngle)) {
            const tiltAngle = `${this.translate.instant('VARIANT_TILE.TILT')} ${this.addUnit({
                value: system.tiltAngle,
                key: 'MEASUREMENT_UNITS.DEGREE'
            })}`;
            nameParts.push(tiltAngle);
        }

        if (!LodashService.isNil(system.ratedPower)) {
            const maskedValue = this.maskValue(system.ratedPower);
            const ratedPower = this.addUnit({ value: maskedValue, key: 'MEASUREMENT_UNITS.KWP', hasSpace: true });
            nameParts.push(ratedPower);
        }

        return (
            nameParts
                .join(', ')
                // Removing first comma;
                .replace(',', '')
        );
    }

    /**
     * @param options.value A value to which unit must be added.
     * @param options.key Phrase key of a unit to be translated and appended.
     * @param options.hasSpace Determines whether to put space between value and unit.
     * @returns The value with an appended unit.
     */
    private addUnit(options: { value: number | string; key: string; hasSpace?: boolean }) {
        const space = options.hasSpace ? ' ' : '';
        return `${options.value}${space}${this.translate.instant(options.key)}`;
    }

    private toPlanningsWithTemplateAttributes(options: {
        variants: VariantGetById[];
        targetVariant: VariantGetById;
        plannings?: PlanningGetById[];
    }): PlanningWithTemplateAttributes[] | undefined {
        const { variants, targetVariant, plannings } = options;
        return plannings?.map(planning => ({
            ...planning,
            hasMenu:
                !VariantsService.isUserVariant(targetVariant as VariantGetById) ||
                this.remoteConfig.config[RemoteConfigParameter.HasUserVariantPlanningOptions],
            isDisabled: VariantsService.isUserVariant(targetVariant as VariantGetById) || !LodashService.isNil(planning.packageNumber),
            planningId: planning.applicationId as ApplicationId,
            duplicationOptions: this.getPlanningDuplicationOptions({ variants, variantToSkipId: targetVariant.id, planning }),
            products: planning.products?.filter(product => product.productType !== ProductType.Other)
        }));
    }

    private maskValue(value: number): string {
        const mask = this.formToolsService.getNumericMask({ numberOfDecimals: '2' });
        const { pattern, decimalMarker } = mask;
        return this.maskPipe.transform(value.toString(), pattern, { decimalMarker });
    }
}
